import { z } from 'zod';

export const businessCategorySchema = z.enum([
  'FOOD_DRINK',
  'RETAIL',
  'SERVICES',
  'HEALTH',
  'TRANSPORT',
  'HOSPITALITY',
  'EDUCATION',
  'PUBLIC_SERVICE',
  'OTHER',
]);

export const businessVerificationStatusSchema = z.enum([
  'UNVERIFIED',
  'PENDING',
  'VERIFIED',
  'REJECTED',
  // A person is looking at it. Distinct from PENDING, which means the business
  // has not yet proved enough — one is our turn and the other is theirs.
  'IN_REVIEW',
  // A badge is a statement about now. These two say it stopped being true,
  // which is not the same as never having been.
  'EXPIRED',
  'REVOKED',
]);

export type BusinessVerificationStatus = z.infer<typeof businessVerificationStatusSchema>;

export const businessSummarySchema = z.object({
  id: z.string(),
  slug: z.string(),
  /** Where /b/:slug redirects to — the one public page a business has. */
  ownerUsername: z.string(),
  name: z.string(),
  category: businessCategorySchema,
  town: z.string(),
  countryCode: z.string(),
  countryFlag: z.string(),
  verificationStatus: businessVerificationStatusSchema,
});

export const businessSchema = businessSummarySchema.extend({
  description: z.string().nullable(),
  countryName: z.string(),
  websiteUrl: z.string().nullable(),
  phone: z.string().nullable(),
  publicEmail: z.string().nullable(),
  openingHours: z.unknown().nullable(),
  verifiedAt: z.string().nullable(),
  tier: z.enum(['FREE', 'LOCAL', 'MULTI']),
  createdAt: z.string(),
  yourRole: z.enum(['OWNER', 'ADMIN', 'RESPONDER']).nullable(),
  /** The CUI or VAT number on file. Null unless you are a member. */
  registrationId: z.string().nullable(),
  /** Where it is registered, when that is not where it trades. Members only. */
  registrationCountry: z.string().nullable(),
  legalName: z.string().nullable(),
  /**
   * What this business may do, decided by the API. The web app deliberately
   * never reimplements the tier table — one source of truth for entitlements.
   */
  capabilities: z.array(z.string()),
});

export const businessListSchema = z.array(businessSummarySchema);

export type Business = z.infer<typeof businessSchema>;
export type BusinessSummary = z.infer<typeof businessSummarySchema>;
